/**
 * OrderImport Component
 *
 * Main container for bulk order import.
 * It orchestrates data fetching, mutations, and conditional view rendering.
 */

import { faDownload, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { cva } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { basketPageSlugs } from '@boss/constants/b2b-b2c';
import { IBasketLine } from '@boss/services/client';
import { DeepPartial } from '@boss/types/b2b-b2c';

import {
  OrderImportArticleSummary,
  OrderImportProcessResponse,
  ProcessedOrderImportLine,
  useCommitOrderImportRows,
  useOrderImportTemplate,
  useProcessOrderImportFile,
  useRevalidateOrderImportRows,
} from '../../client-queries';
import { useBasket } from '../../hooks';
import OrderImportInitialView from './components/OrderImportInitialView';
import OrderImportProcessingView from './components/OrderImportProcessingView';
import type { OrderImportProps, OrderImportStep } from './types';
import { ORDER_IMPORT_UPLOAD_FAILED_PARAMETER } from '../../constants/checkout';

const ShellStyles = cva('w-full mb-8', {
  variants: {
    mode: {
      page: 'flex flex-col gap-6',
      modal: 'max-w-[980px]',
    },
  },
});

const DEFAULT_TEMPLATE_ASSET_TAG = 'orderImportTemplate';

const isSuccessfulImportLine = (row: OrderImportProcessResponse['rows'][number]) => row.errors.length === 0;

type OrderImportEditableField = 'skuId' | 'myMixColorId' | 'quantity';

type OrderImportEditableFieldState = {
  skuId: boolean;
  myMixColorId: boolean;
  quantity: boolean;
};

const recalculateResultSummary = (
  previousResult: OrderImportProcessResponse,
  rows: ProcessedOrderImportLine[],
): OrderImportProcessResponse => {
  const invalidRows = rows.filter(row => row.errors.length > 0).length;

  return {
    ...previousResult,
    rows,
    totalRows: rows.length,
    invalidRows,
    validRows: rows.length - invalidRows,
  };
};

const getEditableFields = (row: ProcessedOrderImportLine): OrderImportEditableFieldState => {
  const hasSkuError = row.errors.some(
    error => error.startsWith('orderImport.errors.sku.') || error === 'orderImport.errors.product.notFound',
  );
  const hasQuantityError = row.errors.some(error => error.startsWith('orderImport.errors.quantity.'));
  const hasColorError = row.errors.some(
    error =>
      error.startsWith('orderImport.errors.color.') ||
      error === 'orderImport.errors.mymix.lookupDependenciesMissing' ||
      error === 'orderImport.errors.mymix.articleNotFound',
  );

  return {
    skuId: hasSkuError,
    myMixColorId: hasColorError,
    quantity: hasQuantityError,
  };
};

const getArticleData = (row: OrderImportProcessResponse['rows'][number]) => {
  const myMixLookup = row.enrichment?.myMix;
  const productLookup = row.enrichment?.product;

  if (myMixLookup?.status === 'ok' && myMixLookup.data) {
    return myMixLookup.data;
  }

  if (productLookup?.status === 'ok' && productLookup.data) {
    return productLookup.data;
  }

  return null;
};

const buildColorPayload = (row: OrderImportProcessResponse['rows'][number], articleData: OrderImportArticleSummary) => {
  const myMixLookup = row.enrichment?.myMix;
  const colorLookup = row.enrichment?.color;
  const colorData = colorLookup?.status === 'ok' ? colorLookup.data : undefined;
  const isMyMix = myMixLookup?.status === 'ok' && !!myMixLookup.data;

  if (!isMyMix && !colorData) {
    return null;
  }

  return {
    colorid: colorData?.colorId ?? '',
    colorcode: colorData?.colorCode ?? articleData.colorCode ?? row.myMixColorId ?? '',
    colorname: colorData?.name ?? '',
    rgb: colorData?.rgb ?? '',
  };
};

const mapImportLineToBasketLine = (
  row: OrderImportProcessResponse['rows'][number],
): DeepPartial<IBasketLine> | null => {
  const articleData = getArticleData(row);

  if (!articleData) {
    return null;
  }

  const colorPayload = buildColorPayload(row, articleData);

  return {
    item: {
      skuid: articleData.skuId,
      description: row.description || articleData.name,
      stockproductnumber: articleData.stockProductNumber,
      stockproductbase: articleData.stockProductBase,
      ...(colorPayload && { color: colorPayload }),
    },
    quantity: {
      salesquantity: row.quantity,
      salesunit: articleData.salesUnit ?? articleData.unit ?? '',
      secondaryquantity: row.quantity,
      secondaryunit: articleData.salesUnit ?? articleData.unit ?? '',
    },
  };
};

const OrderImport = ({
  mode = 'page',
  className,
  onClose,
  onFileSelected,
  templateDownloadHref = '#',
  templateAssetTag = DEFAULT_TEMPLATE_ASSET_TAG,
}: OrderImportProps) => {
  const { locale, push } = useRouter();
  const { t } = useTranslation('account');
  const [processedResult, setProcessedResult] = useState<OrderImportProcessResponse | null>(null);
  const [processingFileName, setProcessingFileName] = useState<string>('');
  const [processingError, setProcessingError] = useState<string>('');
  const [isUploadingProducts, setIsUploadingProducts] = useState(false);

  const { mutateAsync: processOrderImportFile, isLoading: isProcessingFile } = useProcessOrderImportFile();
  const { mutateAsync: revalidateOrderImportRows, isLoading: isRevalidatingRows } = useRevalidateOrderImportRows();
  const { mutateAsync: commitOrderImportRows } = useCommitOrderImportRows();
  const { refetch: fetchTemplate, isFetching: isTemplateFetching } = useOrderImportTemplate(templateAssetTag, {
    enabled: false,
  });
  const { addBasketLines } = useBasket();

  const title = t('orderImport.title');
  const isShowingResults = !!processedResult;
  const isProcessingStateVisible = isProcessingFile || isShowingResults;
  const downloadIcon = isTemplateFetching ? faSpinner : faDownload;
  const displayFileName = processingFileName || processedResult?.fileName || String(t('orderImport.template.fileName'));
  const processingTitle = isProcessingFile
    ? String(t('orderImport.upload.processing'))
    : String(t('orderImport.upload.resultTitle'));

  const isUploadProductsDisabled =
    !processedResult || processedResult.invalidRows > 0 || processedResult.rows.length === 0;

  const handleStartOver = () => {
    setProcessedResult(null);
    setProcessingFileName('');
    setProcessingError('');
    setIsUploadingProducts(false);
  };

  const handleTemplateDownloadClick = async () => {
    const { data } = await fetchTemplate();

    if (data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleFileSelected = async (file: File) => {
    onFileSelected?.(file);
    setProcessingFileName(file.name);
    setProcessingError('');
    setIsUploadingProducts(false);

    try {
      const response = await processOrderImportFile(file);

      setProcessedResult(response);
    } catch (error) {
      setProcessedResult(null);
      const errorMessage = error instanceof Error ? error.message : '';
      const translatedError = errorMessage.startsWith('orderImport.')
        ? String(t(errorMessage))
        : String(t('orderImport.upload.processError'));

      setProcessingError(translatedError);
    }
  };

  const handleDismissError = () => {
    setProcessingError('');
  };

  const redirectToBasketWithError = async () => {
    if (!locale) {
      return;
    }

    await push({
      pathname: basketPageSlugs[locale],
      query: {
        [ORDER_IMPORT_UPLOAD_FAILED_PARAMETER]: 'true',
      },
    });

    if (mode === 'modal') {
      onClose?.();
    }
  };

  const handleUploadProducts = async () => {
    if (!processedResult || isUploadProductsDisabled || isUploadingProducts) {
      return;
    }

    setIsUploadingProducts(true);

    try {
      const commitResponse = await commitOrderImportRows({
        fileName: processedResult.fileName,
        rows: processedResult.rows.map(row => {
          return {
            rowId: row.rowId,
            lineNumber: row.lineNumber,
            skuId: row.skuId,
            quantity: row.quantity,
            description: row.description,
            myMixColorId: row.myMixColorId,
          };
        }),
      });

      const basketLines = commitResponse.result.rows
        .filter(isSuccessfulImportLine)
        .map((row: OrderImportProcessResponse['rows'][number]) => mapImportLineToBasketLine(row))
        .filter((line: DeepPartial<IBasketLine> | null): line is DeepPartial<IBasketLine> => !!line);

      if (!basketLines.length) {
        await redirectToBasketWithError();
        return;
      }

      await addBasketLines(basketLines, true);

      if (mode === 'modal') {
        onClose?.();
        return;
      }

      if (locale) {
        await push(basketPageSlugs[locale]);
      }
    } catch (_error) {
      await redirectToBasketWithError();
    } finally {
      setIsUploadingProducts(false);
    }
  };

  const handleRevalidateRows = async () => {
    if (!processedResult) {
      return;
    }

    try {
      const response = await revalidateOrderImportRows({
        fileName: processedResult.fileName,
        sourceColumns: processedResult.sourceColumns,
        rows: processedResult.rows.map(row => {
          return {
            rowId: row.rowId,
            lineNumber: row.lineNumber,
            skuId: row.skuId,
            quantity: row.quantity,
            description: row.description,
            myMixColorId: row.myMixColorId,
          };
        }),
      });

      setProcessedResult(response);
    } catch (_error) {
      setProcessingError(String(t('orderImport.upload.processError')));
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    if (!processedResult) {
      return;
    }

    const nextRows = processedResult.rows.filter((_row, index) => index !== rowIndex);

    setProcessedResult(recalculateResultSummary(processedResult, nextRows));
  };

  const handleRowFieldChange = ({
    rowIndex,
    field,
    value,
  }: {
    rowIndex: number;
    field: OrderImportEditableField;
    value: string;
  }) => {
    if (!processedResult) {
      return;
    }

    const nextRows = processedResult.rows.map((row, index) => {
      if (index !== rowIndex) {
        return row;
      }

      if (field === 'quantity') {
        const normalizedQuantity = Number(value);

        return {
          ...row,
          quantity: value.trim() === '' || !Number.isFinite(normalizedQuantity) ? undefined : normalizedQuantity,
        };
      }

      return {
        ...row,
        [field]: value,
      };
    });

    setProcessedResult(recalculateResultSummary(processedResult, nextRows));
  };

  const steps: Array<{ id: OrderImportStep; number: number; title: string; description: string }> = [
    {
      id: 'sku-search',
      number: 1,
      title: t('orderImport.steps.downloadTemplate.title'),
      description: t('orderImport.steps.downloadTemplate.description'),
    },
    {
      id: 'mymix-search',
      number: 2,
      title: t('orderImport.steps.uploadFile.title'),
      description: t('orderImport.steps.uploadFile.description'),
    },
    {
      id: 'add-to-basket',
      number: 3,
      title: t('orderImport.steps.reviewAndOrder.title'),
      description: t('orderImport.steps.reviewAndOrder.description'),
    },
  ];

  return (
    <section className={twMerge(ShellStyles({ mode }), className)} data-testid="order-import">
      <h1>{title}</h1>

      {isProcessingStateVisible ? (
        <OrderImportProcessingView
          fileName={displayFileName}
          isLoading={isProcessingFile}
          isRevalidating={isRevalidatingRows}
          isUploadProductsDisabled={isUploadProductsDisabled}
          isUploadingProducts={isUploadingProducts}
          mode={mode}
          onDeleteRow={handleDeleteRow}
          onRevalidate={handleRevalidateRows}
          onRowFieldChange={handleRowFieldChange}
          onStartOver={handleStartOver}
          onUploadProducts={handleUploadProducts}
          result={processedResult}
          title={processingTitle}
          toEditableFields={getEditableFields}
        />
      ) : (
        <OrderImportInitialView
          colorDisclaimer={String(t('orderImport.colorDisclaimer'))}
          description={String(t('orderImport.description'))}
          downloadButtonLabel={String(t('orderImport.template.downloadButton'))}
          downloadIcon={downloadIcon}
          isDownloadDisabled={isTemplateFetching}
          isTemplateLinkLoading={isTemplateFetching}
          onDismissError={handleDismissError}
          onFileSelected={handleFileSelected}
          onTemplateDownloadClick={handleTemplateDownloadClick}
          processingError={processingError}
          steps={steps}
          uploadSupportedText={String(t('orderImport.upload.supported'))}
          uploadTitle={String(t('orderImport.upload.title'))}
        />
      )}
    </section>
  );
};

export default OrderImport;
